import React, {useState} from 'react';
import type {TableColumnsType} from 'antd';
import {Button, Table} from 'antd';
import {OverallStatus, PODReviewStatus} from "../../../../enums/DeliveryEnum";
import {formatTimestampToDateTime} from "../../../../utils/dateTimeUtils";
import {useQuery} from "@apollo/client";
import {allChannelResponse, GET_ALL_CHANNEL} from "../../../../graphql/queries/getChannelAll";
import {OrderType} from "../../../../graphql/types/OrderType";
import { useDspOptions } from '../../../../hooks/useDspOptions';

import { t } from '../../../../i18n';
import {ExceptionDictionaryResponse, GET_ALL_EXCEPTION} from "../../../../graphql/queries/getAllException";
import {useStatusInternationalization} from "../../../delivery/parcels/hooks/useStatusInternationalization";

interface ParcelRowProps {
    parcelData: OrderType[];
    showParcelDetail: (record: OrderType) => void;
    onSelectChange?: (selectedRowKeys: React.Key[], selectedRows: OrderType[]) => void;
    selectedRowKeys?: React.Key[];
    loading?: boolean;
    onSortChange?: (field: string | null, direction: 'ASC' | 'DESC' | null) => void;
}

export const ParcelColumns: React.FC<ParcelRowProps> = ({
    parcelData, 
    showParcelDetail, 
    onSelectChange,
    selectedRowKeys: externalSelectedRowKeys,
    loading,
    onSortChange
}) => {
    const [internalSelectedRowKeys, setInternalSelectedRowKeys] = useState<React.Key[]>([]);
    const selectedRowKeys = externalSelectedRowKeys !== undefined ? externalSelectedRowKeys : internalSelectedRowKeys;
    const {data} = useQuery<allChannelResponse>(GET_ALL_CHANNEL);
    const {data:exceptionData} = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const { dspOptions } = useDspOptions();
    
    // 使用状态国际化hook
    const {
        getStatusText,
        getStatusColor,
        getComplaintText,
        getExceptionText,
        getExceptionStatusText,
        getExceptionReasonText,
        getNextActionTextFromBackend
    } = useStatusInternationalization();

    const handleSelectChange = (newSelectedRowKeys: React.Key[], selectedRows: OrderType[]) => {
        if (onSelectChange) {
            // 如果提供了外部处理函数，调用它
            onSelectChange(newSelectedRowKeys, selectedRows);
        } else {
            // 否则使用内部状态
            setInternalSelectedRowKeys(newSelectedRowKeys);
        }
    };

    // 配置行选择
    const rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
    };

    const parcelColumns: TableColumnsType<OrderType> = [
        {
            title: t('delivery.parcels.label.tracking'),
            dataIndex: 'trackingNumber',
            key: 'trackingNumber',
            align: 'left',
            render: (text) => <Button
                type="link"
                onClick={() => showParcelDetail(parcelData.find(p => p.trackingNumber === text)!)}>
                {text}
            </Button>,
        },
        {
            title: t('delivery.parcels.label.externalTrackingNumber'),
            dataIndex: 'externalTrackingNumber',
            key: 'externalTrackingNumber',
            align: 'left',
            render: (text: string | null | undefined) => (
                <span style={{ whiteSpace: 'nowrap' }}>{text || '-'}</span>
            ),
        },
        {
            title: t('delivery.parcels.label.order'),
            dataIndex: 'channelOrderNumber',
            key: 'channelOrderNumber',
            align: 'left',
        },
        {
            title: t('delivery.parcels.label.client'),
            dataIndex: 'channelId',
            key: 'channelId',
            align: 'left',
            render: (text, record) => {
                const channel = data?.getAllChannels?.find(
                    (channel) => String(channel.id) === String(record.channelId)
                );
                return channel ? channel.channelName : '-';
            },
        },
        {
            title: t('delivery.parcels.label.status'),
            dataIndex: 'overallStatus',
            key: 'overallStatus',
            align: 'left',
            render: (status) => {
                const statusText = getStatusText(status);
                const color = getStatusColor(status);
                
                return <span className={`inline-block px-2 py-1 rounded ${color}`}>{statusText}</span>;
            },
        },
        {
            title: t('delivery.parcels.label.podReviewStatus'),
            dataIndex: 'podReviewStatus',
            key: 'podReviewStatus',
            align: 'left',
            render: (value) => {
                if (value === undefined || value === null) return '-';
                const statusMap = {
                    [PODReviewStatus.NOT_REVIEWED]: t('enum.podReviewStatus.NOT_REVIEWED'),
                    [PODReviewStatus.MACHINE_APPROVED]: t('enum.podReviewStatus.MACHINE_APPROVED'),
                    [PODReviewStatus.MACHINE_DISAPPROVED]: t('enum.podReviewStatus.MACHINE_DISAPPROVED'),
                    [PODReviewStatus.MANUAL_PENDING]: t('enum.podReviewStatus.MANUAL_PENDING'),
                    [PODReviewStatus.MANUAL_APPROVED]: t('enum.podReviewStatus.MANUAL_APPROVED'),
                    [PODReviewStatus.MANUAL_DISAPPROVED]: t('enum.podReviewStatus.MANUAL_DISAPPROVED'),
                    //特殊处理 人审误判修正视作人审通过  展示也作为人审通过展示
                    [PODReviewStatus.MANUAL_CORRECTED]: t('enum.podReviewStatus.MANUAL_APPROVED')
                };
                return statusMap[value as keyof typeof statusMap] || t('enum.podReviewStatus.UNKNOWN');
            },
        },
        {
            title: t('delivery.parcels.label.prePickupHub'),
            dataIndex: 'prePickupHubName',
            key: 'prePickupHubName',
            align: 'left',
            render: (value) => value || '-',
        },
        {
            title: t('delivery.parcels.label.pickupHub'),
            dataIndex: 'pickupHubName',
            key: 'pickupHubName',
            align: 'left',
            render: (value) => value || '-',
        },
        {
            title: t('delivery.parcels.label.firstSortCode'),
            key: 'sortingCodeSegment1',
            align: 'left',
            render: (_,record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[0] || '';
            },
        },
        {
            title: t('delivery.parcels.label.secondSortCode'),
            key: 'sortingCodeSegment2',
            align: 'left',
            render: (_,record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[1] || '';
            },
        },
        {
            title: t('delivery.parcels.label.thirdSortCode'),
            key: 'sortingCodeSegment3',
            align: 'left',
            render: (_,record) => {
                const parts = record.sortingCode?.split('-') || [];
                return parts[2] || '';
            },
        },
        {
            title: t('delivery.parcels.label.receiverZipcode'),
            dataIndex: 'receiverZipcode',
            key: 'receiverZipcode',
            align: 'left',
            render: (text) => text || '-',
        },
        {
            title: t('delivery.parcels.label.dsp'),
            dataIndex: 'deliveredDspId',
            key: 'deliveredDspId',
            align: 'left',
            render: (dspId) => {
                if (!dspId) return '-';
                const dsp = dspOptions?.find(option =>
                    String(option.value) === String(dspId)
                );
                return dsp ? `${dsp.label}` : '-';
            },
        },
        {
            title: t('delivery.parcels.label.pickupTaskDate'),
            dataIndex: 'receivedAtHub',
            key: 'receivedAtHub',
            align: 'left',
            sorter: (a, b) => {
                const av = a.receivedAtHub ? new Date(a.receivedAtHub as unknown as string).getTime() : 0;
                const bv = b.receivedAtHub ? new Date(b.receivedAtHub as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.hubReceiveDate'),
            dataIndex: 'destHubArrivedAt',
            key: 'destHubArrivedAt',
            align: 'left',
            sorter: (a, b) => {
                const av = a.destHubArrivedAt ? new Date(a.destHubArrivedAt as unknown as string).getTime() : 0;
                const bv = b.destHubArrivedAt ? new Date(b.destHubArrivedAt as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.whsReceiveDate'),
            dataIndex: 'destWarehouseArrivedAt',
            key: 'destWarehouseArrivedAt',
            align: 'left',
            sorter: (a, b) => {
                const av = a.destWarehouseArrivedAt ? new Date(a.destWarehouseArrivedAt as unknown as string).getTime() : 0;
                const bv = b.destWarehouseArrivedAt ? new Date(b.destWarehouseArrivedAt as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.lastTaskDate'),
            dataIndex: 'lastDeliveryScanTime',
            key: 'lastDeliveryScanTime',
            align: 'left',
            sorter: (a, b) => {
                const av = a.lastDeliveryScanTime ? new Date(a.lastDeliveryScanTime as unknown as string).getTime() : 0;
                const bv = b.lastDeliveryScanTime ? new Date(b.lastDeliveryScanTime as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.lastDeliveryScanTime'),
            dataIndex: 'lastDeliveryScanTime',
            key: 'lastDeliveryScanTime2',
            align: 'left',
            sorter: (a, b) => {
                const av = a.lastDeliveryScanTime ? new Date(a.lastDeliveryScanTime as unknown as string).getTime() : 0;
                const bv = b.lastDeliveryScanTime ? new Date(b.lastDeliveryScanTime as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.deliveredAt'),
            dataIndex: 'deliveredAt',
            key: 'deliveredAt',
            align: 'left',
            sorter: (a, b) => {
                const av = a.deliveredAt ? new Date(a.deliveredAt as unknown as string).getTime() : 0;
                const bv = b.deliveredAt ? new Date(b.deliveredAt as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.hasException'),
            dataIndex: 'isHasBeenSetException',
            key: 'isHasBeenSetException',
            align: 'left',
            render: (value) => getExceptionText(value),
        },
        {
            title: t('delivery.parcels.label.exceptionStaus'),
            dataIndex: 'isAbnormal',
            key: 'isAbnormal',
            align: 'left',
            render: (text, record) => {
              if (!record.isHasBeenSetException) {
                return '-';
              }
              return getExceptionStatusText(text, record.exceptionStatus);
            }
        },
        {
            title: t('delivery.parcels.label.lastExceptionReason'),
            dataIndex: 'exceptionReasonCode',
            key: 'exceptionReasonCode',
            align: 'left',
            render: (text, record) => {
                const exception = exceptionList?.find(
                    (exception) => String(exception.exceptionReasonCode) === String(record.exceptionReasonCode)
                );
                return getExceptionReasonText(exception, record.exceptionReasonCode);
            }
        },
        {
            title: t('delivery.parcels.label.lastExceptionDate'),
            dataIndex: 'markExceptionAt',
            key: 'markExceptionAt',
            align: 'left',
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.nextAction'),
            dataIndex: 'nextAction',
            key: 'nextAction',
            align: 'left',
            render: (value) => getNextActionTextFromBackend(value),
        },
        {
            title: t('delivery.parcels.label.orderTime'),
            dataIndex: 'orderTime',
            key: 'orderTime',
            align: 'left',
            sorter: (a, b) => {
                const av = a.orderTime ? new Date(a.orderTime as unknown as string).getTime() : 0;
                const bv = b.orderTime ? new Date(b.orderTime as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        },
        {
            title: t('delivery.parcels.label.bagTaskNumber'),
            dataIndex: 'bagTaskNumber',
            key: 'bagTaskNumber',
            align: 'left',
            render: (text) => text || '-',
        },
        {
            title: t('delivery.parcels.label.isComplaint'),
            dataIndex: 'isComplaint',
            key: 'isComplaint',
            align: 'left',
            render: (value) => getComplaintText(value),
        },
        {
            title: t('central.parcels.filters.returnArrivalHubAt'),
            dataIndex: 'returnArrivalHubAt',
            key: 'returnArrivalHubAt',
            align: 'center',
            sorter: (a, b) => {
                const av = a.returnArrivalHubAt ? new Date(a.returnArrivalHubAt as unknown as string).getTime() : 0;
                const bv = b.returnArrivalHubAt ? new Date(b.returnArrivalHubAt as unknown as string).getTime() : 0;
                return av - bv;
            },
            render: (text) => text ? formatTimestampToDateTime(text) : '-',
        }
    ];

    const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
        if (!onSortChange) return;
        const order = sorter?.order as ('ascend' | 'descend' | undefined);
        const columnKey = sorter?.columnKey as (string | undefined);
        if (!order || !columnKey) {
            onSortChange(null, null);
            return;
        }
        // 映射列key到后端字段
        const keyToField: Record<string, string> = {
            deliveredAt: 'deliveredAt',
            lastDeliveryScanTime: 'lastDeliveryScanTime',
            lastDeliveryScanTime2: 'lastDeliveryScanTime',
            destHubArrivedAt: 'destHubArrivedAt',
            destWarehouseArrivedAt: 'destWarehouseArrivedAt',
            orderTime: 'orderTime',
            receivedAtHub: 'receivedAtHub',
            returnArrivalHubAt: 'returnArrivalHubAt'
        };
        const field = keyToField[columnKey] || 'orderTime';
        const direction = order === 'ascend' ? 'ASC' : 'DESC';
        onSortChange(field, direction);
    };

    return (
        <Table
            rowSelection={rowSelection}
            rowKey="trackingNumber"
            columns={parcelColumns}
            dataSource={parcelData}
            scroll={{x: 2500}}
            size="small"
            className="mb-4"
            pagination={false}
            loading={loading}
            onChange={handleTableChange}
        />
    );
};